import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';

// Cores funcionais Kahoot (correspondem ao painel admin)
const ALTERNATIVE_COLORS = ['bg-danger hover:bg-danger/90', 'bg-primary hover:bg-primary/90', 'bg-warning hover:bg-warning/90', 'bg-success hover:bg-success/90'];

const QuestionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { session, currentQuestion, questionIndex, totalQuestions, timeRemaining, timerSeconds, hasAnswered, setHasAnswered, setLastAnswerFeedback, questionResult, isQuestionClosed, questionRevealed, sessionReset, clearSessionReset, subscribe } =
    useLiveChallenge();

  const [submitting, setSubmitting] = useState(false);
  const [polledChallenge, setPolledChallenge] = useState(null);
  // Lembra se o aluno chegou a ver o timer rodando na questão atual (timeRemaining>0).
  // Distingue dois estados que terminam ambos em timer=0 sem resposta:
  //   - aluno legítimo que viu a Q e estourou o tempo → mostra "Tempo esgotado!"
  //     em cima da Q (mensagem informativa);
  //   - aluno tardio que escaneou QR depois do timer zerar → placeholder neutro
  //     (decisão de produto para Cenário 15: não revelar a Q que ele não pôde ver).
  const [hasSeenActiveTimer, setHasSeenActiveTimer] = useState(false);
  const lastQuestionIdRef = useRef(null);
  const pollingRef = useRef(null);
  const challengeId = id || session?.live_challenge_id;

  const activeQuestion = currentQuestion;
  const alternatives = activeQuestion?.['live-challenge-alternatives'] || activeQuestion?.live_challenge_alternatives || activeQuestion?.alternatives || [];
  const activeTimerMax = timerSeconds || polledChallenge?.timer_seconds || 30;

  // F5 fallback: na navegação normal (JoinPage → WaitingRoom → QuestionPage) o
  // subscribe é chamado em JoinPage. Em F5 essa página é remontada direto, sem
  // passar por JoinPage — session é null, WS nunca conecta, currentQuestion
  // permanece null e o aluno fica preso em "Aguardando o professor revelar a
  // questão...". Subscribe é idempotente: se já conectado a este challenge, no-op.
  useEffect(() => {
    if (challengeId && !session) {
      subscribe(challengeId, 'student');
    }
  }, [challengeId, session, subscribe]);

  useEffect(() => {
    // Reset quando muda de questão (inclusive de null pra Q1 no primeiro mount).
    if (activeQuestion?.id !== lastQuestionIdRef.current) {
      lastQuestionIdRef.current = activeQuestion?.id ?? null;
      setHasSeenActiveTimer(false);
      return;
    }
    // Marca que viu timer ativo nesta questão. Não reseta ao chegar a 0 — é um
    // latch para a questão corrente.
    if (timeRemaining > 0 && questionRevealed) {
      setHasSeenActiveTimer(true);
    }
  }, [activeQuestion?.id, timeRemaining, questionRevealed]);

  // showFeedback: aluno respondeu OU admin encerrou a Q. Mantém a Q visível com
  // caixa de feedback ("Resposta enviada!" / "Questão encerrada antes da sua
  // resposta."). Sem isso, o question_closed broadcast (que seta questionRevealed
  // =false) fazia a UI piscar pro placeholder por 1 frame antes do useEffect de
  // questionResult navegar pra /resultado.
  const showFeedback = hasAnswered || isQuestionClosed;
  // showLocalTimeout: aluno legítimo cujo timer expirou (viu o timer > 0 nesta Q)
  // mas o broadcast question_closed ainda não chegou. Mantém a Q visível com
  // "Tempo esgotado!" — diferente do isQuestionClosed (broadcast oficial) ele é
  // só client-side, baseado no timer local.
  const showLocalTimeout = !showFeedback && questionRevealed && timeRemaining <= 0 && hasSeenActiveTimer;
  // Esconde a questão (placeholder neutro) quando:
  //   1. ainda não há current_question (esperando primeiro reveal);
  //   2. há current_question mas o admin não revelou (entrou entre questões);
  //   3. timer <= 0 E aluno nunca viu o timer rodar (aluno tardio via QR — Cenário 15).
  const isWaitingForQuestion = !activeQuestion || (!showFeedback && !showLocalTimeout && (!questionRevealed || timeRemaining <= 0));

  useEffect(() => {
    const pollStatus = async () => {
      if (document.hidden) return;
      try {
        const response = await liveChallengesApi.show(challengeId);
        const raw = response?.data;
        if (!raw?.attributes) return;
        const attrs = Object.fromEntries(Object.entries(raw.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]));
        setPolledChallenge(attrs);
        if (attrs.status === 'finished') {
          clearInterval(pollingRef.current);
          navigate(`/desafio-ao-vivo/${challengeId}/final`, { replace: true });
        }
      } catch {
        // ignore
      }
    };
    pollStatus();
    pollingRef.current = setInterval(pollStatus, 3000);
    const onVisible = () => {
      if (!document.hidden) pollStatus();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(pollingRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [challengeId, navigate]);

  useEffect(() => {
    if (questionResult) {
      navigate(`/desafio-ao-vivo/${challengeId}/resultado`, { replace: true });
    }
  }, [questionResult, navigate, challengeId]);

  // Admin reiniciou a sessão — participante foi destruído no backend, volta ao
  // /entrar para join novo.
  useEffect(() => {
    if (sessionReset) {
      clearSessionReset();
      navigate('/desafio-ao-vivo/entrar', { replace: true });
    }
  }, [sessionReset, clearSessionReset, navigate]);

  const handleAnswer = useCallback(
    async alternativeId => {
      if (hasAnswered || submitting || isQuestionClosed || timeRemaining <= 0) {
        if (isQuestionClosed && !hasAnswered) {
          // eslint-disable-next-line no-console
          console.info('[LiveChallenge answer] descartado — questão já encerrada antes do click chegar');
          toast('A questão foi encerrada antes da sua resposta.', { icon: '⏱️' });
        }
        return;
      }
      setSubmitting(true);

      const submit = () => liveChallengesApi.answer(challengeId, alternativeId);
      const isRetryable = err => {
        const code = err?.code;
        const status = err?.response?.status;
        // Retry só em network/timeout (sem response do server) ou 5xx.
        return code === 'ECONNABORTED' || code === 'ERR_NETWORK' || (status >= 500 && status < 600);
      };

      try {
        let response;
        try {
          response = await submit();
        } catch (e1) {
          if (!isRetryable(e1)) throw e1;
          // eslint-disable-next-line no-console
          console.warn('[LiveChallenge answer] retry após falha de rede', { code: e1?.code, status: e1?.response?.status });
          await new Promise(r => setTimeout(r, 1000));
          response = await submit();
        }
        const feedback = response?.data?.data ?? response?.data ?? response;
        setHasAnswered(true);
        setLastAnswerFeedback(feedback);
      } catch (error) {
        const data = error?.response?.data || {};
        // eslint-disable-next-line no-console
        console.warn('[LiveChallenge answer] falhou', {
          challengeId,
          alternativeId,
          currentQuestionId: activeQuestion?.id,
          validAlternativeIds: alternatives.map(a => a.id),
          status: error?.response?.status,
          code: error?.code,
          payload: data
        });
        let message;
        if (data.code === 'not_found') {
          message = 'Esta resposta não é mais válida — a questão já foi encerrada.';
        } else if (data.code === 'invalid') {
          message = data.error || 'Não foi possível registrar sua resposta.';
        } else if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK') {
          message = 'Sem conexão. Verifique sua internet e tente novamente.';
        } else {
          message = data.error || data.message || 'Erro ao enviar resposta.';
        }
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    },
    [hasAnswered, submitting, timeRemaining, isQuestionClosed, challengeId, setHasAnswered, setLastAnswerFeedback, activeQuestion, alternatives]
  );

  const isDisabled = hasAnswered || submitting || isQuestionClosed || timeRemaining <= 0;
  const qIndex = questionIndex || polledChallenge?.current_question_index || 0;
  const qTotal = totalQuestions || polledChallenge?.total_questions || '?';
  const isUrgent = timeRemaining <= 5;
  const progress = activeTimerMax > 0 ? (timeRemaining / activeTimerMax) * 100 : 0;

  if (isWaitingForQuestion) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-bright px-6">
        <div className="flex flex-col items-center gap-4">
          <div className="flex gap-2">
            <span className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:0ms]" />
            <span className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:150ms]" />
            <span className="h-3 w-3 animate-bounce rounded-full bg-primary [animation-delay:300ms]" />
          </div>
          <p className="m-0 text-base font-semibold text-foreground">Aguardando o professor revelar a questão...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-bright px-4 py-6">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <header className="flex items-center justify-between">
          <span className="text-sm font-semibold text-neutral">
            Questão {qIndex + 1} de {qTotal}
          </span>

          <div className="relative flex h-16 w-16 items-center justify-center">
            <svg
              className="h-16 w-16 -rotate-90"
              viewBox="0 0 100 100"
            >
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                className="text-neutral-lightest"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeDasharray={`${2 * Math.PI * 45}`}
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress / 100)}`}
                strokeLinecap="round"
                className={`transition-all duration-1000 ease-linear ${isUrgent ? 'text-danger' : 'text-primary'}`}
              />
            </svg>
            <span className={`absolute text-base font-bold ${isUrgent ? 'text-danger' : 'text-foreground'}`}>{timeRemaining}</span>
          </div>
        </header>

        <div className="rounded-2xl bg-white px-6 py-8 text-center shadow-dropshadow-md">
          <h2 className="m-0 text-xl font-bold text-foreground md:text-2xl">{activeQuestion?.description}</h2>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {alternatives.map((alt, index) => {
            const colorClasses = ALTERNATIVE_COLORS[index % ALTERNATIVE_COLORS.length];
            return (
              <button
                key={alt.id}
                type="button"
                onClick={() => handleAnswer(alt.id)}
                disabled={isDisabled}
                className={`flex items-center gap-3 rounded-xl p-5 text-left text-base font-bold text-white shadow-dropshadow-md transition-transform duration-150 ease-out-strong active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 ${colorClasses}`}
              >
                <span className="text-lg opacity-80">{String.fromCharCode(65 + index)}</span>
                <span className="flex-1">{alt.description}</span>
              </button>
            );
          })}
        </div>

        {(hasAnswered || isQuestionClosed || showLocalTimeout) && (
          <div className="rounded-xl bg-white px-6 py-4 text-center shadow-dropshadow-sm">
            {hasAnswered ? (
              <p className="m-0 text-base font-semibold text-primary">Resposta enviada! Aguardando os outros...</p>
            ) : isQuestionClosed ? (
              <p className="m-0 text-base font-semibold text-warning">Questão encerrada antes da sua resposta.</p>
            ) : (
              <p className="m-0 text-base font-semibold text-danger">Tempo esgotado!</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionPage;
